import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Award,
  Globe,
  Users2,
  CheckCircle2,
  Circle,
  Clock,
  Target,
  TrendingUp,
  Zap,
  Rocket,
  Code,
  Database,
  Shield,
  Building2,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronRight,
  Star,
  Sparkles,
  DollarSign
} from 'lucide-react';
import { Link } from 'react-router-dom';

const roadmapPhases = [
  {
    year: '2025',
    quarter: 'Q1-Q4',
    phase: 'Capability Building',
    title: 'Vendor Certifications & Team Growth',
    tagline: 'Building the most qualified IT infrastructure team in Japan',
    description: 'Investing in our engineers through top-tier vendor certifications and strategic team expansion, ensuring every client project is backed by certified expertise in networking, security, and AV systems.',
    icon: Award,
    gradient: 'from-blue-500 to-cyan-500',
    status: 'in-progress',
    progress: 70,
    impact: ['12 Certifications', '25-person team', '98% CSAT'],
    milestones: [
      {
        id: 'm1',
        title: 'Cisco CCNP Certification',
        description: 'Core engineers achieve Cisco CCNP Enterprise and Security certifications for advanced network deployments',
        status: 'completed',
        date: 'Q1 2025',
        icon: Shield,
        details: [
          'CCNP Enterprise for complex routing and switching',
          'CCNP Security for firewall and VPN deployments',
          'Advanced SD-WAN and campus network expertise'
        ]
      },
      {
        id: 'm2',
        title: 'Fortinet NSE Certification',
        description: 'Security team completes Fortinet NSE 4, 5, and 7 certification tracks for enterprise firewall projects',
        status: 'in-progress',
        date: 'Q2-Q3 2025',
        icon: Award,
        details: [
          'NSE 4 Network Security Professional',
          'NSE 5 Network Security Analyst',
          'FortiGate and FortiManager deployment expertise'
        ]
      },
      {
        id: 'm3',
        title: 'AV & Collaboration Certifications',
        description: 'AV team earns Poly, Cisco Webex, and Microsoft Teams Rooms certifications for conferencing projects',
        status: 'upcoming',
        date: 'Q4 2025',
        icon: Star,
        details: [
          'Poly G-series certified installation',
          'Cisco Webex Room systems deployment',
          'Microsoft Teams Rooms design and integration'
        ]
      }
    ],
    technologies: ['Cisco IOS-XE', 'FortiOS', 'Poly AV Suite', 'MS Teams Rooms', 'Cisco Webex'],
    team: 25,
    investment: '¥15M'
  },
  {
    year: '2026',
    quarter: 'Q1-Q4',
    phase: 'Regional Expansion',
    title: 'Osaka & Nagoya Operations',
    tagline: 'Bringing enterprise IT services closer to our clients',
    description: 'Establishing dedicated regional offices in Osaka and Nagoya to serve growing demand for on-site IT infrastructure services across western and central Japan, reducing response times and expanding our reach.',
    icon: Building2,
    gradient: 'from-purple-500 to-pink-500',
    status: 'upcoming',
    progress: 0,
    impact: ['2 New offices', '40+ engineers', '50+ new clients'],
    milestones: [
      {
        id: 'm4',
        title: 'Site Selection & Setup',
        description: 'Identify and establish office and staging depot locations in Osaka and Nagoya city centers',
        status: 'upcoming',
        date: 'Q1 2026',
        icon: MapPin,
        details: [
          'Osaka office: Umeda business district',
          'Nagoya office: Meieki area',
          'Local vendor and logistics partnerships secured'
        ]
      },
      {
        id: 'm5',
        title: 'Regional Team Hiring',
        description: 'Recruit and onboard certified field engineers and project managers for each regional office',
        status: 'upcoming',
        date: 'Q2-Q3 2026',
        icon: Users2,
        details: [
          '15-20 certified field engineers per region',
          'Project managers and site leads hired',
          'Logistics and warehousing coordinators onboarded'
        ]
      },
      {
        id: 'm6',
        title: 'Regional Operations Launch',
        description: 'Full regional operations live with local client onboarding and first project deliveries',
        status: 'upcoming',
        date: 'Q4 2026',
        icon: Rocket,
        details: [
          'First 10 enterprise clients onboarded per region',
          'Local warehousing and equipment staging depot open',
          'Integrated operations with Tokyo headquarters'
        ]
      }
    ],
    technologies: ['Field service management', 'Asset tracking', 'Project management tools', 'Remote monitoring'],
    team: 60,
    investment: '¥30M'
  },
  {
    year: '2027',
    quarter: 'Q1-Q4',
    phase: 'Service Evolution',
    title: 'Managed IT Services Launch',
    tagline: 'From project delivery to long-term IT partnership',
    description: 'Launching a comprehensive managed services offering including 24/7 network monitoring, on-call engineering support, hardware maintenance contracts, and proactive IT management for enterprise clients.',
    icon: Zap,
    gradient: 'from-green-500 to-emerald-500',
    status: 'upcoming',
    progress: 0,
    impact: ['24/7 monitoring', '100+ managed clients', 'SLA-backed support'],
    milestones: [
      {
        id: 'm7',
        title: 'Managed Service Design',
        description: 'Define service tiers, SLAs, pricing, and operational workflows for managed IT delivery',
        status: 'upcoming',
        date: 'Q1-Q2 2027',
        icon: Target,
        details: [
          'Bronze / Silver / Gold service tiers defined',
          'SLA commitments: 99.9% uptime guarantee',
          'Escalation and incident response playbooks'
        ]
      },
      {
        id: 'm8',
        title: 'NOC & Platform Build',
        description: 'Stand up a dedicated Network Operations Center with monitoring and ITSM ticketing platform',
        status: 'upcoming',
        date: 'Q2-Q3 2027',
        icon: Database,
        details: [
          'Network monitoring via Zabbix and PRTG',
          'ITSM ticketing system integration',
          'Remote access and patch management tools'
        ]
      },
      {
        id: 'm9',
        title: 'Client Rollout',
        description: 'Migrate first 30 existing project clients to managed services contracts with dedicated account management',
        status: 'upcoming',
        date: 'Q4 2027',
        icon: Sparkles,
        details: [
          'Priority onboarding for long-term clients',
          'Monthly reporting and Quarterly Business Reviews',
          'Dedicated account managers assigned per client'
        ]
      }
    ],
    technologies: ['Zabbix', 'PRTG', 'ServiceNow', 'Meraki Dashboard', 'FortiManager'],
    team: 45,
    investment: '¥25M'
  },
  {
    year: '2028',
    quarter: 'Q1-Q4',
    phase: 'International Growth',
    title: 'APAC Market Expansion',
    tagline: 'Delivering enterprise IT infrastructure across Asia Pacific',
    description: 'Expanding beyond Japan to serve enterprise clients in Singapore, South Korea, and Southeast Asia, bringing our proven IT infrastructure delivery model to the broader APAC market.',
    icon: Globe,
    gradient: 'from-orange-500 to-red-500',
    status: 'upcoming',
    progress: 0,
    impact: ['3+ APAC markets', '200+ projects', '¥1B revenue target'],
    milestones: [
      {
        id: 'm10',
        title: 'APAC Market Entry',
        description: 'Establish legal entities and partner networks in Singapore and South Korea',
        status: 'upcoming',
        date: 'Q1-Q2 2028',
        icon: Globe,
        details: [
          'Singapore entity setup and vendor agreements',
          'South Korea operations launch',
          'Regional partner agreements signed'
        ]
      },
      {
        id: 'm11',
        title: 'Local Partnerships',
        description: 'Build channel partnerships with regional IT distributors and system integrators',
        status: 'upcoming',
        date: 'Q2-Q3 2028',
        icon: Building2,
        details: [
          'Cisco Gold Partner network access in region',
          'Regional distributor agreements finalized',
          'Local warehousing and logistics established'
        ]
      },
      {
        id: 'm12',
        title: 'APAC Regional Teams',
        description: 'Hire and certify regional engineering teams to deliver infrastructure projects locally',
        status: 'upcoming',
        date: 'Q3-Q4 2028',
        icon: Users2,
        details: [
          '20+ certified engineers per APAC market',
          'Local project management staff in place',
          'APAC-wide managed services coverage live'
        ]
      }
    ],
    technologies: ['Multi-region NOC', 'Regional compliance tools', 'ITSM platforms', 'Vendor portals'],
    team: 80,
    investment: '¥50M'
  }
];

const timelineYears = ['2025', '2026', '2027', '2028'];

const Roadmap = () => {
  const [activeYear, setActiveYear] = useState('2025');
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'grid'

  const activePhase = roadmapPhases.find(p => p.year === activeYear);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in-progress':
        return 'text-blue-600 bg-blue-100';
      case 'upcoming':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return CheckCircle2;
      case 'in-progress':
        return Clock;
      case 'upcoming':
        return Circle;
      default:
        return Circle;
    }
  };

  return (
    <>
      <Helmet>
        <title>Our Roadmap - Tasks IT Services | Strategic Growth Plan</title>
        <meta
          name="description"
          content="Explore the strategic growth roadmap of Tasks IT Services — from vendor certifications and regional expansion to managed IT services and APAC market entry."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[32rem] h-[32rem] bg-blue-600/25 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" />

        {/* Hero */}
        <section className="relative pt-20 pb-12 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20">
                <Sparkles className="h-4 w-4 text-cyan-300" />
                <span className="text-sm font-medium">Our Journey Ahead</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
                Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">Roadmap</span>
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Our strategic plan for growing capabilities, regional reach, and managed IT services across Japan and APAC
              </p>

              {/* View Mode Toggle */}
              <div className="mt-8 inline-flex gap-2 bg-white/10 backdrop-blur-sm p-1.5 rounded-xl border border-white/20">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'timeline'
                      ? 'bg-white text-blue-600 shadow-lg'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Timeline View
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                    viewMode === 'grid'
                      ? 'bg-white text-blue-600 shadow-lg'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Grid View
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {viewMode === 'timeline' ? (
              <>
                {/* Year Selector */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center gap-4 mb-12"
                >
                  {timelineYears.map((year) => {
                    const phase = roadmapPhases.find(p => p.year === year);
                    return (
                      <button
                        key={year}
                        onClick={() => setActiveYear(year)}
                        className={`relative px-8 py-4 rounded-2xl font-bold text-lg transition-all ${
                          activeYear === year
                            ? 'bg-white text-blue-600 shadow-2xl scale-105'
                            : 'backdrop-blur-xl bg-white/10 border border-white/20 text-white hover:bg-white/20'
                        }`}
                      >
                        <div className="text-2xl">{year}</div>
                        <div className="text-xs opacity-80 mt-1">{phase.quarter}</div>
                        {phase.status === 'in-progress' && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </motion.div>

                {/* Active Phase Details */}
                <AnimatePresence mode="wait">
                  {activePhase && (
                    <motion.div
                      key={activePhase.year}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                      className="space-y-8"
                    >
                      {/* Phase Header */}
                      <div className="relative group">
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/30 via-purple-500/20 to-cyan-500/30 blur-xl opacity-60 group-hover:opacity-90 transition" />
                        <div className="relative backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 text-white">
                          <div className="flex items-start justify-between mb-6">
                            <div className="flex-1">
                              <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${activePhase.gradient} rounded-2xl mb-4 shadow-xl`}>
                                <activePhase.icon className="h-8 w-8 text-white" />
                              </div>
                              <div className="text-sm font-semibold text-cyan-300 mb-2">{activePhase.phase}</div>
                              <h2 className="text-4xl font-bold mb-2">{activePhase.title}</h2>
                              <p className="text-xl text-blue-200 mb-4">{activePhase.tagline}</p>
                              <p className="text-blue-100 leading-relaxed max-w-3xl">{activePhase.description}</p>
                            </div>

                            {activePhase.status === 'in-progress' && (
                              <div className="ml-6">
                                <div className="w-32 h-32 relative">
                                  <svg className="transform -rotate-90 w-32 h-32">
                                    <circle
                                      cx="64"
                                      cy="64"
                                      r="56"
                                      stroke="currentColor"
                                      strokeWidth="8"
                                      fill="none"
                                      className="text-white/20"
                                    />
                                    <circle
                                      cx="64"
                                      cy="64"
                                      r="56"
                                      stroke="currentColor"
                                      strokeWidth="8"
                                      fill="none"
                                      strokeDasharray={`${2 * Math.PI * 56}`}
                                      strokeDashoffset={`${2 * Math.PI * 56 * (1 - activePhase.progress / 100)}`}
                                      className="text-cyan-400"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-3xl font-bold">{activePhase.progress}%</span>
                                  </div>
                                </div>
                                <div className="text-center text-sm text-blue-200 mt-2">Progress</div>
                              </div>
                            )}
                          </div>

                          {/* Impact Metrics */}
                          <div className="grid grid-cols-3 gap-4 mb-6">
                            {activePhase.impact.map((metric, i) => (
                              <div key={i} className="backdrop-blur-sm bg-white/10 rounded-xl p-4 border border-white/20">
                                <TrendingUp className="h-5 w-5 text-cyan-300 mb-2" />
                                <div className="text-2xl font-bold mb-1">{metric.split(' ')[0]}</div>
                                <div className="text-sm text-blue-200">{metric.split(' ').slice(1).join(' ')}</div>
                              </div>
                            ))}
                          </div>

                          {/* Meta Info */}
                          <div className="flex flex-wrap gap-6 text-sm">
                            <div className="flex items-center gap-2">
                              <Users2 className="h-4 w-4 text-cyan-300" />
                              <span className="text-blue-200">Team:</span>
                              <span className="font-semibold">{activePhase.team} members</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-green-300" />
                              <span className="text-blue-200">Investment:</span>
                              <span className="font-semibold">{activePhase.investment}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Code className="h-4 w-4 text-purple-300" />
                              <span className="text-blue-200">Tech Stack:</span>
                              <span className="font-semibold">{activePhase.technologies.slice(0, 2).join(', ')}...</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Milestones */}
                      <div className="space-y-4">
                        {activePhase.milestones.map((milestone, idx) => {
                          const StatusIcon = getStatusIcon(milestone.status);
                          const isExpanded = expandedMilestone === milestone.id;
                          
                          return (
                            <motion.div
                              key={milestone.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="relative group"
                            >
                              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/10 to-cyan-500/20 blur-xl opacity-0 group-hover:opacity-70 transition" />
                              <div className="relative backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl overflow-hidden">
                                <button
                                  onClick={() => setExpandedMilestone(isExpanded ? null : milestone.id)}
                                  className="w-full p-6 text-left text-white"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-4 flex-1">
                                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                        <milestone.icon className="h-6 w-6" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                          <h3 className="text-xl font-bold">{milestone.title}</h3>
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`}>
                                            <StatusIcon className="h-3 w-3 inline mr-1" />
                                            {milestone.status.replace('-', ' ')}
                                          </span>
                                        </div>
                                        <p className="text-blue-100 text-sm mb-2">{milestone.description}</p>
                                        <div className="flex items-center gap-2 text-sm text-blue-200">
                                          <Calendar className="h-4 w-4" />
                                          {milestone.date}
                                        </div>
                                      </div>
                                    </div>
                                    <motion.div
                                      animate={{ rotate: isExpanded ? 90 : 0 }}
                                      className="ml-4"
                                    >
                                      <ChevronRight className="h-5 w-5 text-blue-200" />
                                    </motion.div>
                                  </div>
                                </button>

                                <AnimatePresence>
                                  {isExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      className="px-6 pb-6"
                                    >
                                      <div className="pl-16 pt-4 border-t border-white/10">
                                        <h4 className="text-sm font-semibold text-white mb-3">Key Deliverables:</h4>
                                        <ul className="space-y-2">
                                          {milestone.details.map((detail, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-blue-100">
                                              <CheckCircle2 className="h-4 w-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                                              {detail}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            ) : (
              /* Grid View */
              <div className="grid md:grid-cols-2 gap-8">
                {roadmapPhases.map((phase, index) => (
                  <motion.div
                    key={phase.year}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/25 via-purple-500/15 to-cyan-500/25 blur-xl opacity-50 group-hover:opacity-80 transition" />
                    <div className="relative backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 text-white h-full">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-14 h-14 bg-gradient-to-r ${phase.gradient} rounded-2xl flex items-center justify-center shadow-xl`}>
                          <phase.icon className="h-7 w-7 text-white" />
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold">{phase.year}</div>
                          <div className="text-sm text-blue-200">{phase.quarter}</div>
                        </div>
                      </div>

                      <div className="text-xs font-semibold text-cyan-300 mb-2">{phase.phase}</div>
                      <h3 className="text-2xl font-bold mb-2">{phase.title}</h3>
                      <p className="text-blue-100 text-sm mb-4 leading-relaxed">{phase.description}</p>

                      {phase.status === 'in-progress' && (
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-blue-200">Progress</span>
                            <span className="font-bold">{phase.progress}%</span>
                          </div>
                          <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${phase.progress}%` }}
                              transition={{ duration: 1, delay: index * 0.2 }}
                              className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="text-xs font-semibold text-blue-200">Impact:</div>
                        {phase.impact.map((metric, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <Zap className="h-3 w-3 text-yellow-300" />
                            <span className="text-blue-100">{metric}</span>
                          </div>
                        ))}
                      </div>

                      <div className="pt-4 border-t border-white/10">
                        <div className="text-xs text-blue-200 mb-2">{phase.milestones.length} Key Milestones</div>
                        <button
                          onClick={() => {
                            setViewMode('timeline');
                            setActiveYear(phase.year);
                          }}
                          className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 hover:text-cyan-200"
                        >
                          View Details
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-4">Partner With Us on This Journey</h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Discover how our growing capabilities will support your IT infrastructure needs today and for years to come
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/contact">
                  <button className="px-8 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold hover:shadow-2xl transition-all group">
                    Request a Consultation
                    <ArrowRight className="inline ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/services">
                  <button className="px-8 py-4 rounded-xl backdrop-blur-sm bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-all">
                    Explore Our Services
                  </button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Roadmap;